import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  fetchOnboardingApi,
  getMyProfileApi,
  saveOnboardingApi,
} from '~/features/profile/api/profile.api';

import type { MyProfile, OnboardingResult } from '~/features/profile/types/profile.type';

export interface ProfileState {
  onboarding: OnboardingResult | null;
  /** 내 신원·역할. 로그인 전이거나 프로필 미생성이면 null. */
  me: MyProfile | null;
  /** `me` 조회를 끝냈는지 — 역할 가드가 판정 전에 튕기지 않도록 필요하다. */
  isMeLoaded: boolean;
  /**
   * 조회를 한 번이라도 끝냈는지.
   * @description `onboarding === null` 만으로는 "아직 안 불러옴"과 "등록 안 함"이 구분되지 않아
   *  온보딩 유도 배너가 로딩 중 잠깐 깜빡인다. 판정 근거를 따로 둔다.
   */
  isLoaded: boolean;
  isSaving: boolean;
}

const initialState: ProfileState = {
  onboarding: null,
  me: null,
  isMeLoaded: false,
  isLoaded: false,
  isSaving: false,
};

export const fetchOnboarding = createAsyncThunk<OnboardingResult | null, void>(
  'profile/fetchOnboarding',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchOnboardingApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchMyProfile = createAsyncThunk<MyProfile | null, void>(
  'profile/fetchMyProfile',
  async (_, { rejectWithValue }) => {
    try {
      return await getMyProfileApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const saveOnboarding = createAsyncThunk<OnboardingResult, OnboardingResult>(
  'profile/saveOnboarding',
  async (result, { rejectWithValue }) => {
    try {
      return await saveOnboardingApi(result);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const ProfileSlice = createSlice({
  initialState,
  name: 'profile',
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchOnboarding.fulfilled, (state, action) => {
        state.onboarding = action.payload;
        state.isLoaded = true;
      })
      .addCase(fetchOnboarding.rejected, state => {
        // 실패도 "확인은 끝난" 상태로 둔다 — 무한 로딩보다 온보딩 유도를 보여주는 편이 낫다.
        state.isLoaded = true;
      })
      .addCase(fetchMyProfile.fulfilled, (state, action) => {
        state.me = action.payload;
        state.isMeLoaded = true;
      })
      .addCase(fetchMyProfile.rejected, state => {
        // 확인은 끝났다 — 실패를 로딩으로 남기면 역할 가드가 영원히 대기한다.
        state.isMeLoaded = true;
      })
      .addCase(saveOnboarding.pending, state => {
        state.isSaving = true;
      })
      .addCase(saveOnboarding.fulfilled, (state, action) => {
        state.onboarding = action.payload;
        state.isLoaded = true;
        state.isSaving = false;
      })
      .addCase(saveOnboarding.rejected, state => {
        state.isSaving = false;
      });
  },
});

export default ProfileSlice;
